import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Form,
} from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function Stock() {
  const [stockEntries, setStockEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    fetchStockEntries();
  }, []);

  const fetchStockEntries = () => {
    setLoading(true);
    api
      .get("/api/stock", {
        withCredentials: true,
      })
      .then((res) => {
        setStockEntries(res.data.stockEntries || []);
      })
      .catch(() => {
        setStockEntries([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filteredEntries = stockEntries.filter((item) => {
    const term = search.toLowerCase();
    return (
      (item.product_name || "").toLowerCase().includes(term) ||
      (item.category_name || "").toLowerCase().includes(term)
    );
  });

  const totalRows = filteredEntries.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEntries = showAll
    ? filteredEntries
    : filteredEntries.slice(startIndex, startIndex + rowsPerPage);

  const grandTotal = filteredEntries.reduce(
    (sum, item) => sum + Number(item.total_value || 0),
    0,
  );

  const exportToExcel = () => {
    const escapeCsvValue = (value) => {
      const text = String(value ?? "");
      const safeText = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
      return `"${safeText.replace(/"/g, '""')}"`;
    };
    const rows = [
      ["Product Name", "Category Name", "Unit", "Total Qty", "Stock Price", "Total Value"],
      ...filteredEntries.map((item) => [
        item.product_name || "",
        item.category_name || "",
        item.unit || "",
        Number(item.quantity || 0),
        Number(item.stock_price || 0),
        Number(item.total_value || 0),
      ]),
      ["", "", "", "", "Total", grandTotal],
    ];
    const csv = rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "product-stock.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product Management", path: "/product" },
          { name: "Stock Summary", path: "/products-stock" },
          { name: "Current Stock Details" },
        ]}
      />

      <Card body className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Card.Title className="mb-0">All Stock</Card.Title>

          <div className="d-flex align-items-center gap-2 ms-auto">
            <input
              type="text"
              placeholder="Search Product / Category"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "250px",
                height: "38px",
                paddingLeft: "10px",
                border: "1px solid #d1d1d1",
              }}
            />
            <Button
              onClick={exportToExcel}
              title="Export stock to Excel"
              aria-label="Export stock to Excel"
              style={{ height: "38px", backgroundColor: "#198754", border: "none" }}
            >
              <i className="i-File-CSV text-18 me-1"></i>
              Excel
            </Button>
          </div>
        </div>
      </Card>

      <Card body>
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover className="align-middle">
            <thead style={{ backgroundColor: "#cfc7d9" }}>
              <tr>
                <th>Product Name</th>
                <th>Category Name</th>
                <th>Unit</th>
                <th>Total Qty</th>
                <th>Stock Price</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="6" className="text-center">Loading...</td>
                </tr>
              )}

              {!loading && paginatedEntries.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No entries found</td>
                </tr>
              )}

              {!loading &&
                paginatedEntries.map((item) => {
                  const quantity = Number(item.quantity || 0);
                  return (
                    <tr key={item.product_id}>
                      <td
                        className="text-white fw-bold"
                        style={{
                          backgroundColor: quantity > 0 ? "#12a154" : "#a11212",
                        }}
                      >
                        {item.product_name || "-"}
                      </td>
                      <td>{item.category_name || "-"}</td>
                      <td>{item.unit || "-"}</td>
                      <td>{quantity}</td>
                      <td>{item.stock_price != null ? `₹${formatAmount(item.stock_price)}` : "-"}</td>
                      <td>₹{formatAmount(item.total_value)}</td>
                    </tr>
                  );
                })}
            </tbody>
            {!loading && filteredEntries.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-end fw-bold">Total:</td>
                  <td className="fw-bold">{formatAmount(grandTotal)}</td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>
          <Form.Select
            value={showAll ? "all" : rowsPerPage}
            onChange={(e) => {
              if (e.target.value === "all") {
                setShowAll(true);
                setRowsPerPage(filteredEntries.length || 10);
              } else {
                setShowAll(false);
                setRowsPerPage(Number(e.target.value));
              }
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value="all">All</option>
          </Form.Select>

          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Prev
          </Button>
          <span className="mx-3">
            {currentPage} / {totalPages || 1}
          </span>
          <Button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>

        <div className="mt-3">
          <Button variant="secondary" onClick={() => window.history.back()}>
            Back
          </Button>
        </div>
      </Card>
    </div>
  );
}
